import { useState } from 'react';
import { X, Check, Loader2 } from 'lucide-react';

const EspaceNumeriqueModal = ({ isOpen, onClose, language = 'fr', coconName = 'COCON SACRÉ', bgColor = '#516258' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/espace-numerique/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(language === 'fr' 
          ? 'Votre demande a été envoyée. Vous recevrez bientôt les informations pour accéder à votre espace privé.'
          : 'Your request has been sent. You will soon receive the information to access your private space.'
        );
      } else {
        setStatus('error');
        setMessage(data.detail || (language === 'fr' ? 'Une erreur est survenue.' : 'An error occurred.'));
      }
    } catch (error) {
      setStatus('error');
      setMessage(language === 'fr' ? 'Une erreur est survenue. Veuillez réessayer.' : 'An error occurred. Please try again.');
    }
  };

  const texts = {
    fr: {
      title: 'MON ESPACE NUMÉRIQUE',
      subtitle: 'My Digital Space',
      p1: `Cet espace numérique est réservé aux coconé.e.s déjà inscrit.e.s à ${coconName}.`,
      p2: 'Pour y accéder, indique simplement l\'adresse email utilisée lors de ton inscription.',
      p3: 'Nous t\'enverrons ensuite les informations nécessaires pour entrer dans ton espace privé.',
      placeholder: 'Email',
      button: 'J\'accède à mon espace',
      loading: 'Envoi en cours...',
    },
    en: {
      title: 'MY DIGITAL SPACE',
      subtitle: 'Mon Espace Numérique',
      p1: `This digital space is reserved for coconé.e.s already registered for ${coconName}.`,
      p2: 'To access it, simply enter the email address used during your registration.',
      p3: 'You will then receive the information needed to enter your private space.',
      placeholder: 'Email',
      button: 'Access my space',
      loading: 'Sending...',
    }
  };

  const t = texts[language] || texts.fr;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" 
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" 
        style={{ backgroundColor: bgColor }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X className="w-5 h-5 text-white/80" />
        </button>

        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h4 className="text-[#F3E5CF] text-xl font-serif tracking-[0.1em] mb-1">
              {t.title}
            </h4>
            <p className="text-[#F3E5CF]/50 text-xs tracking-[0.15em] uppercase">
              {t.subtitle}
            </p>
          </div>

          {status === 'success' ? (
            // Success state
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-[#C5A059]" />
              </div>
              <p className="text-[#F3E5CF] text-sm leading-relaxed">
                {message}
              </p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2 text-[#F3E5CF]/70 text-sm hover:text-[#F3E5CF] transition-colors"
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </button>
            </div>
          ) : (
            // Form state
            <>
              <div className="text-sm text-[#F3E5CF]/90 mb-8 space-y-4 leading-relaxed">
                <p>{t.p1}</p>
                <p>{t.p2}</p>
                <p className="text-[#3F2A24] italic">{t.p3}</p>
              </div>

              <form onSubmit={handleSubmit}>
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.placeholder}
                  required 
                  autoComplete="email" 
                  className="w-full px-4 py-3 border border-[#F3E5CF]/30 rounded-lg bg-white/10 text-[#F3E5CF] text-sm mb-4 placeholder-[#F3E5CF]/50 focus:outline-none focus:border-[#C5A059] transition-colors" 
                  disabled={status === 'loading'}
                />
                
                {status === 'error' && (
                  <p className="text-red-300 text-xs mb-4 text-center">{message}</p>
                )}

                <button 
                  type="submit" 
                  disabled={status === 'loading'}
                  className="w-full py-3 bg-[#C5A059] text-[#3F2A24] font-medium text-sm tracking-[0.1em] rounded-lg hover:bg-[#F3E5CF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.loading}
                    </>
                  ) : (
                    t.button
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EspaceNumeriqueModal;
